import { getLbtcAddressConfig, OEnv } from '@lombard.finance/sdk';
import { useBoringVaultV1 } from 'boring-vault-ui';
import { getValidChain, OLstToken, TChainId } from 'modules/api';
import { useConnection } from 'modules/auth';

import { FormEventHandler, useEffect, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { WITHDRAW_DAYS_VALID, WITHDRAW_DISCOUNT_PERCENT } from '../const';
import { BTC_DECIMALS } from 'modules/common/const';
import { useVaultProvider } from 'modules/dashboard/components/Deposit/VaultContextProvider/hooks/useVaultProvider';
import { useRefetchWithdrawalStatus } from './useRefetchWithdrawalStatus';
import { useGetWithdrawalStatusQuery } from 'modules/dashboard/actions/getWithdrawalStatus';

export interface IWithdrawalFormValues {
  chain: TChainId;
  amount: string;
  txId: string;
}

interface IWithdrawalForm {
  isLoading: boolean;
  status?: {
    txId: string;
    amount: string;
  };
  form: UseFormReturn<IWithdrawalFormValues>;
  onSubmit: FormEventHandler<HTMLFormElement>;
}

const lbtcConfig = getLbtcAddressConfig(OEnv.prod);

const WITHDRAWAL_STATUS_POLLING_INTERVAL = 10_000; // 10 seconds

export function useWithdrawalForm(): IWithdrawalForm {
  const [isLoading, setLoading] = useState(false);
  const { address, chainId: currentChainId } = useConnection();
  const refetchWithdrawalStatus = useRefetchWithdrawalStatus(
    address,
    getValidChain(currentChainId),
  );

  const form = useForm<IWithdrawalFormValues>({
    defaultValues: {
      chain: getValidChain(currentChainId),
      amount: '',
      txId: '',
    },
  });

  const chain = form.watch('chain');

  useEffect(() => {
    form.setValue('chain', getValidChain(currentChainId));
  }, [currentChainId]);

  const { isBoringV1ContextReady, queueBoringWithdraw } = useBoringVaultV1();

  useGetWithdrawalStatusQuery(
    { address, chainId: getValidChain(currentChainId) },
    {
      pollingInterval: WITHDRAWAL_STATUS_POLLING_INTERVAL,
      skip: !address || !currentChainId,
    },
  );

  const { provider } = useVaultProvider();

  const onSubmit = form.handleSubmit(async values => {
    setLoading(true);

    if (!isBoringV1ContextReady || !provider) {
      setLoading(false);
      return;
    }

    const chainId = getValidChain(chain);
    const signer = await provider.getSigner();

    try {
      if (chainId in lbtcConfig) {
        const address = lbtcConfig[chainId as keyof typeof lbtcConfig];

        const result = await queueBoringWithdraw(
          signer,
          values.amount,
          {
            displayName: OLstToken.LBTC,
            address,
            decimals: BTC_DECIMALS,
          },
          WITHDRAW_DISCOUNT_PERCENT,
          WITHDRAW_DAYS_VALID,
        );
        console.log('result', result);
        await refetchWithdrawalStatus();
      }
    } finally {
      setLoading(false);
    }
  });

  return {
    form,
    isLoading,
    onSubmit,
  };
}
